/**
 * Lexical to Mdast Visitor for Secret Field
 * Exports SecretFieldNode to markdown AST
 *
 * When a noteKey is available in memory for the current note, plaintext
 * content is encrypted before being written to the mdast tree. Already-
 * encrypted content (`enc:…`) passes through unchanged.
 */

import { $isSecretFieldNode, SecretFieldNode } from './SecretFieldNode';
import type { LexicalExportVisitor } from '@mdxeditor/editor';
import { useNoteKeyStore } from '@/store/noteKeyStore';
import {
  isEncryptedFieldValue,
  encryptFieldValue,
} from '@/services/CryptoService';
import { getCurrentNoteId } from './secretFieldContext';

/**
 * Visitor that converts SecretFieldNode to mdast (markdown AST)
 * Only supports inline format
 */
export const SecretFieldVisitor: LexicalExportVisitor<SecretFieldNode, any> = {
  testLexicalNode: $isSecretFieldNode,
  visitLexicalNode({ lexicalNode, mdastParent }) {
    const label = lexicalNode.getLabel();
    let content = lexicalNode.getContent();

    // --- Encrypt plaintext content if possible ---
    const noteId = getCurrentNoteId();
    if (noteId && content && !isEncryptedFieldValue(content)) {
      const noteKey = useNoteKeyStore.getState().getNoteKey(noteId);
      if (noteKey) {
        try {
          content = encryptFieldValue(content, noteKey);
        } catch {
          // Encryption failed — leave content as-is.
          // The save-guard (item 7) will block persistence.
        }
      }
    }

    // Inline format: ::secret[Label]{content}::
    // Use textDirective for inline format
    mdastParent.children.push({
      type: 'textDirective',
      name: 'secret',
      attributes: label ? { label } : {},
      children: [
        {
          type: 'text',
          value: content
        }
      ]
    });
  }
};

